/**
 * Export Excel du widget "Couverture des commandes".
 *
 * Répond à la question : « ai-je assez de stock pour servir les commandes en
 * attente ? ». Mêmes données et même sémantique que la page Pilotage de
 * préparation (`getPilotageProduitData`), base **stock éligible** :
 *   - commandé = besoin des commandes ouvertes sur l'horizon
 *   - déjà réservé = engagé en zone prépa
 *   - reste = commandé − déjà réservé
 *   - réservable maintenant = min(reste, stock éligible)
 *   - manque = max(0, reste − stock éligible)  → à produire / acheter
 *   - % couvrable = (déjà réservé + éligible) / commandé
 *
 * 4 feuilles :
 *   1. **Synthèse** : contexte (horizon / filtres), KPI cards (couverts /
 *      à réserver / en manque / totaux), doughnut répartition par statut.
 *   2. **Tous les produits** : 1 ligne / produit, toutes les colonnes.
 *   3. **En manque** : produits dont le stock éligible ne couvre pas le besoin
 *      (tri par urgence = 1ʳᵉ date d'expédition), pour la prod / les achats.
 *   4. **À réserver** : produits entièrement couvrables mais pas encore engagés
 *      (action du jour), tri par urgence.
 *
 * Branding & style alignés sur `lot-par-etat-excel.utils.ts` (palette + helpers
 * partagés `addJustupBranding` / `setJustupMetadata`).
 */
import * as ExcelJS from "exceljs";
import {
  Chart,
  ArcElement,
  DoughnutController,
  Legend,
  Tooltip,
} from "chart.js";
import {
  addJustupBranding,
  setJustupMetadata,
} from "../../../../shared/utils/excel-branding.util";
import {
  CouvertureCommandeRow,
  CouvertureExportContext,
  CouvertureStatut as Statut,
  CouvertureDerived as Derived,
  COUVERTURE_STATUT_LABEL,
  deriveCouverture,
  toUV,
} from "./couverture-commandes.model";

export type { CouvertureCommandeRow, CouvertureExportContext };

Chart.register(ArcElement, DoughnutController, Legend, Tooltip);

// ─────────────────────────────────────────────────────────────────────────────
// Palette (alignée lot-par-etat-excel.utils.ts)
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  border: "FFD0D5DD",
  rowBorder: "FFE4E7EC",
  titleColor: "FF1B2A4A",
  // Statuts (pastel lisible sur fond clair Excel)
  statGreen: "FFE6FFF5",
  statOrange: "FFFFF0E0",
  statRed: "FFFFEAEA",
  statBlue: "FFE6F0FF",
  statPurple: "FFF0E6FF",
  // Texte statut vif
  textGreen: "FF0C8346",
  textOrange: "FFCC4D02",
  textRed: "FFC60F26",
};

// Couleurs doughnut par statut (Couvert / À réserver / Manque)
const STATUT_COLORS: Record<Statut, string> = {
  COUVERT: "#11C85F",
  A_RESERVER: "#AD05FF",
  MANQUE: "#C60F26",
};

const STATUT_LABEL = COUVERTURE_STATUT_LABEL;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (dérivés mutualisés dans couverture-commandes.model.ts)
// ─────────────────────────────────────────────────────────────────────────────
const derive = deriveCouverture;

function contrainteLabel(t: CouvertureCommandeRow["typeDateLimite"]): string {
  if (t === "DDM") return "DDM";
  if (t === "DLC") return "DLC";
  if (t === "AUCUNE") return "Aucune";
  return "—";
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** Tri par urgence : 1ʳᵉ date d'expédition ASC (sans date = en dernier). */
function byUrgence(
  a: CouvertureCommandeRow,
  b: CouvertureCommandeRow,
): number {
  const da = a.premierDeparture ?? "9999-12-31";
  const db = b.premierDeparture ?? "9999-12-31";
  return da.localeCompare(db);
}

function renderStatutDoughnut(counts: Record<Statut, number>): string | null {
  try {
    const entries = (Object.keys(counts) as Statut[]).filter(
      (k) => counts[k] > 0,
    );
    if (entries.length === 0) return null;
    const canvas = document.createElement("canvas");
    canvas.width = 460;
    canvas.height = 460;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const chart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: entries.map((k) => `${STATUT_LABEL[k]} (${counts[k]})`),
        datasets: [
          {
            data: entries.map((k) => counts[k]),
            backgroundColor: entries.map((k) => STATUT_COLORS[k]),
            borderWidth: 1,
            borderColor: "#fff",
          },
        ],
      },
      options: {
        responsive: false,
        animation: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: { font: { size: 12 }, padding: 10 },
          },
          tooltip: { enabled: false },
        },
      },
    });
    chart.update();
    const png = canvas.toDataURL("image/png");
    chart.destroy();
    return png.replace(/^data:image\/png;base64,/, "");
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Feuille 1 — Synthèse
// ─────────────────────────────────────────────────────────────────────────────
async function buildSyntheseSheet(
  wb: ExcelJS.Workbook,
  rows: CouvertureCommandeRow[],
  ctx: CouvertureExportContext,
): Promise<void> {
  const ws = wb.addWorksheet("Synthèse");
  ws.columns = [
    { width: 34 },
    { width: 20 },
    { width: 20 },
    { width: 20 },
  ];

  // Titre
  ws.mergeCells("A1:C1");
  const title = ws.getCell("A1");
  title.value = "Couverture des commandes";
  title.font = { size: 16, bold: true, color: { argb: C.titleColor } };
  title.alignment = { horizontal: "left", vertical: "middle" };
  ws.getRow(1).height = 28;

  // Contexte (horizon + filtres + date génération)
  const gen = ctx.generatedAt ?? new Date();
  const contextLines: Array<[string, string]> = [
    ["Horizon", ctx.horizonLabel],
    ["Clients", ctx.clientsLabel ?? "Tous"],
    ["Groupes de clients", ctx.groupesLabel ?? "Tous"],
    [
      "Généré le",
      gen.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    ],
  ];
  let r = 2;
  for (const [label, value] of contextLines) {
    ws.getCell(`A${r}`).value = label;
    ws.getCell(`A${r}`).font = { size: 10, italic: true, color: { argb: "FF667085" } };
    ws.getCell(`B${r}`).value = value;
    ws.getCell(`B${r}`).font = { size: 10, color: { argb: "FF101828" } };
    r++;
  }

  // Agrégats
  const d = rows.map(derive);
  const nbProduits = rows.length;
  const nbCouvert = d.filter((x) => x.statut === "COUVERT").length;
  const nbAReserver = d.filter((x) => x.statut === "A_RESERVER").length;
  const nbManque = d.filter((x) => x.statut === "MANQUE").length;
  const sumBesoin = rows.reduce((s, x) => s + x.besoinUG, 0);
  const sumEngage = rows.reduce((s, x) => s + x.engageUG, 0);
  const sumEligible = rows.reduce((s, x) => s + Math.max(0, x.stockEligibleUG), 0);
  const sumReservable = d.reduce((s, x) => s + x.reservableNow, 0);
  const sumManque = d.reduce((s, x) => s + x.manque, 0);
  const sumEnOF = rows.reduce((s, x) => s + Math.max(0, x.qtyEnOFUG), 0);
  const couvrableGlobalPct =
    sumBesoin > 0
      ? Math.round(
          (Math.min(sumBesoin, sumEngage + sumEligible) / sumBesoin) * 100,
        )
      : 100;
  // Commandes impactées par un manque (cumul produits — pas un distinct global)
  const cmdManque = rows
    .filter((_, i) => d[i].statut === "MANQUE")
    .reduce((s, x) => s + x.nbCommandes, 0);

  r += 1;
  ws.mergeCells(`A${r}:C${r}`);
  ws.getCell(`A${r}`).value = "Indicateurs clés";
  ws.getCell(`A${r}`).font = { size: 12, bold: true, color: { argb: C.titleColor } };
  ws.getRow(r).height = 22;
  r++;

  const kpis: Array<[string, string | number, string]> = [
    ["Produits suivis", nbProduits, C.statBlue],
    ["% couvrable global", `${couvrableGlobalPct} %`, couvrableGlobalPct >= 100 ? C.statGreen : couvrableGlobalPct > 0 ? C.statOrange : C.statRed],
    ["✓ Produits couverts", nbCouvert, C.statGreen],
    ["À réserver (produits)", nbAReserver, C.statPurple],
    ["✗ Produits en manque", nbManque, C.statRed],
    ["Total à réserver (UG)", Math.round(sumReservable * 100) / 100, C.statPurple],
    ["Total manquant (UG)", Math.round(sumManque * 100) / 100, C.statRed],
    ["En production (UG)", Math.round(sumEnOF * 100) / 100, C.statBlue],
    ["Commandes impactées par un manque", cmdManque, C.statRed],
  ];
  for (const [label, value, bg] of kpis) {
    const cl = ws.getCell(`A${r}`);
    cl.value = label;
    cl.font = { size: 11, color: { argb: C.titleColor } };
    cl.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
    cl.border = { bottom: { style: "thin", color: { argb: C.border } } };
    const cv = ws.getCell(`B${r}`);
    cv.value = value;
    cv.font = { size: 12, bold: true, color: { argb: C.titleColor } };
    cv.alignment = { horizontal: "right" };
    cv.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
    cv.border = { bottom: { style: "thin", color: { argb: C.border } } };
    if (typeof value === "number") cv.numFmt = "#,##0.##";
    r++;
  }

  // Doughnut répartition par statut
  const png = renderStatutDoughnut({
    COUVERT: nbCouvert,
    A_RESERVER: nbAReserver,
    MANQUE: nbManque,
  });
  if (png) {
    ws.getCell(`A${r + 1}`).value = "Répartition des produits par statut";
    ws.getCell(`A${r + 1}`).font = {
      size: 12,
      bold: true,
      color: { argb: C.titleColor },
    };
    const imageId = wb.addImage({ base64: png, extension: "png" });
    ws.addImage(imageId, {
      tl: { col: 0, row: r + 1 } as any,
      ext: { width: 360, height: 360 },
    });
  }

  await addJustupBranding(wb, ws, 4);
}

// ─────────────────────────────────────────────────────────────────────────────
// Colonnes (descripteurs réutilisables)
// ─────────────────────────────────────────────────────────────────────────────
interface ColDef {
  header: string;
  width: number;
  value: (row: CouvertureCommandeRow, d: Derived) => string | number | null;
  numFmt?: string;
  align?: "left" | "right" | "center";
}

const COL: Record<string, ColDef> = {
  ref: { header: "Référence", width: 14, value: (r) => r.reference ?? "—" },
  article: {
    header: "Article",
    width: 38,
    value: (r) => r.denomination ?? "—",
  },
  statut: {
    header: "Statut",
    width: 14,
    value: (_r, d) => STATUT_LABEL[d.statut],
    align: "center",
  },
  contrainte: {
    header: "Contrainte",
    width: 11,
    value: (r) => contrainteLabel(r.typeDateLimite),
    align: "center",
  },
  contrainteJours: {
    header: "Durée (j)",
    width: 10,
    value: (r) => (r.dluoJours != null ? r.dluoJours : null),
    numFmt: "#,##0",
    align: "right",
  },
  commandeUG: {
    header: "Commandé (UG)",
    width: 14,
    value: (r) => r.besoinUG,
    numFmt: "#,##0.##",
    align: "right",
  },
  commandeUV: {
    header: "Commandé (UV)",
    width: 14,
    value: (r) => r.besoinUV,
    numFmt: "#,##0.##",
    align: "right",
  },
  uv: { header: "UV", width: 8, value: (r) => r.uniteVente ?? "—", align: "center" },
  dejaReserve: {
    header: "Déjà réservé (UG)",
    width: 16,
    value: (r) => r.engageUG,
    numFmt: "#,##0.##",
    align: "right",
  },
  eligible: {
    header: "Stock éligible (UG)",
    width: 17,
    value: (r) => Math.max(0, r.stockEligibleUG),
    numFmt: "#,##0.##",
    align: "right",
  },
  stockTotal: {
    header: "Stock total (UG)",
    width: 15,
    value: (r) => Math.max(0, r.stockTotalUG),
    numFmt: "#,##0.##",
    align: "right",
  },
  reservable: {
    header: "Réservable maint. (UG)",
    width: 20,
    value: (_r, d) => d.reservableNow,
    numFmt: "#,##0.##",
    align: "right",
  },
  manqueUG: {
    header: "Manque (UG)",
    width: 13,
    value: (_r, d) => d.manque,
    numFmt: "#,##0.##",
    align: "right",
  },
  manqueUV: {
    header: "Manque (UV)",
    width: 13,
    value: (r, d) => toUV(d.manque, r.coefficient),
    numFmt: "#,##0.##",
    align: "right",
  },
  enProd: {
    header: "En production (UG)",
    width: 16,
    value: (r) => Math.max(0, r.qtyEnOFUG),
    numFmt: "#,##0.##",
    align: "right",
  },
  couvrable: {
    header: "% couvrable",
    width: 12,
    value: (_r, d) => d.couvrablePct / 100,
    numFmt: "0 %",
    align: "right",
  },
  nbCmd: {
    header: "Nb cmd",
    width: 9,
    value: (r) => r.nbCommandes,
    numFmt: "#,##0",
    align: "right",
  },
  nbLignes: {
    header: "Nb lignes",
    width: 10,
    value: (r) => r.nbLignes,
    numFmt: "#,##0",
    align: "right",
  },
  reliquat: {
    header: "Lignes à revoir",
    width: 13,
    value: (r) => r.nbLignesARevoir,
    numFmt: "#,##0",
    align: "right",
  },
  premiere: {
    header: "1ʳᵉ expédition",
    width: 14,
    value: (r) => fmtDate(r.premierDeparture),
    align: "center",
  },
  dateExpedition: {
    header: "Date expédition",
    width: 15,
    value: (r) => fmtDate(r.premiereExpedition),
    align: "center",
  },
  dateLivraison: {
    header: "Date livraison",
    width: 15,
    value: (r) => fmtDate(r.premiereLivraison),
    align: "center",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Construction générique d'une feuille tableau
// ─────────────────────────────────────────────────────────────────────────────
async function buildTableSheet(
  wb: ExcelJS.Workbook,
  name: string,
  rows: CouvertureCommandeRow[],
  cols: ColDef[],
  opts: { colorByStatut?: boolean } = {},
): Promise<void> {
  const ws = wb.addWorksheet(name);
  ws.columns = cols.map((c) => ({ header: c.header, width: c.width }));

  // En-tête
  const head = ws.getRow(1);
  head.font = { bold: true, color: { argb: C.headerFont } };
  head.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: C.headerBg },
  };
  head.alignment = { vertical: "middle", horizontal: "left" };
  head.height = 22;

  for (const row of rows) {
    const d = derive(row);
    const values = cols.map((c) => c.value(row, d));
    const r = ws.addRow(values);
    r.eachCell((cell, colNumber) => {
      const def = cols[colNumber - 1];
      cell.border = {
        top: { style: "thin", color: { argb: C.rowBorder } },
        bottom: { style: "thin", color: { argb: C.rowBorder } },
      };
      if (def.numFmt) cell.numFmt = def.numFmt;
      if (def.align) cell.alignment = { horizontal: def.align };
      cell.font = { size: 10, color: { argb: "FF101828" } };
      // Colorisation de la cellule Statut
      if (opts.colorByStatut && def.header === "Statut") {
        const bg =
          d.statut === "COUVERT"
            ? C.statGreen
            : d.statut === "MANQUE"
              ? C.statRed
              : C.statPurple;
        const fg =
          d.statut === "COUVERT"
            ? C.textGreen
            : d.statut === "MANQUE"
              ? C.textRed
              : "FFAD05FF";
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
        cell.font = { size: 10, bold: true, color: { argb: fg } };
      }
    });
  }

  ws.views = [{ state: "frozen", ySplit: 1 }];
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: cols.length },
  };
  await addJustupBranding(wb, ws, cols.length);
}

// ─────────────────────────────────────────────────────────────────────────────
// Entrée publique
// ─────────────────────────────────────────────────────────────────────────────
export async function exportCouvertureCommandesExcel(
  rows: CouvertureCommandeRow[],
  ctx: CouvertureExportContext,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, {
    title: "Couverture des commandes",
    subject: "Besoins produits vs stock pour les commandes en attente",
    description:
      "Couverture des commandes en attente (commandé / réservé / éligible / " +
      "manque) — extrait du dashboard Justup",
  });

  // Feuille 1 — Synthèse
  await buildSyntheseSheet(wb, rows, ctx);

  // Feuille 2 — Tous les produits (toutes colonnes)
  const allCols: ColDef[] = [
    COL.ref,
    COL.article,
    COL.statut,
    COL.contrainte,
    COL.contrainteJours,
    COL.commandeUG,
    COL.commandeUV,
    COL.uv,
    COL.dejaReserve,
    COL.eligible,
    COL.stockTotal,
    COL.reservable,
    COL.manqueUG,
    COL.enProd,
    COL.couvrable,
    COL.nbCmd,
    COL.nbLignes,
    COL.reliquat,
    COL.dateExpedition,
    COL.dateLivraison,
  ];
  // Tri "tous" : manque d'abord (urgence), puis à réserver, puis couverts.
  const statutRank: Record<Statut, number> = {
    MANQUE: 0,
    A_RESERVER: 1,
    COUVERT: 2,
  };
  const allSorted = [...rows].sort((a, b) => {
    const ra = statutRank[derive(a).statut];
    const rb = statutRank[derive(b).statut];
    if (ra !== rb) return ra - rb;
    return byUrgence(a, b);
  });
  await buildTableSheet(wb, "Tous les produits", allSorted, allCols, {
    colorByStatut: true,
  });

  // Feuille 3 — En manque (à produire / acheter), tri urgence
  const manqueRows = rows
    .filter((row) => derive(row).manque > 0)
    .sort(byUrgence);
  if (manqueRows.length > 0) {
    await buildTableSheet(wb, "En manque", manqueRows, [
      COL.ref,
      COL.article,
      COL.contrainte,
      COL.contrainteJours,
      COL.manqueUG,
      COL.manqueUV,
      COL.commandeUG,
      COL.dejaReserve,
      COL.eligible,
      COL.enProd,
      COL.nbCmd,
      COL.dateExpedition,
      COL.dateLivraison,
    ]);
  }

  // Feuille 4 — À réserver (couvrable, pas encore engagé), tri urgence
  const aReserverRows = rows
    .filter((row) => {
      const d = derive(row);
      return d.statut === "A_RESERVER" && d.reservableNow > 0;
    })
    .sort(byUrgence);
  if (aReserverRows.length > 0) {
    await buildTableSheet(wb, "À réserver", aReserverRows, [
      COL.ref,
      COL.article,
      COL.reservable,
      COL.eligible,
      COL.dejaReserve,
      COL.commandeUG,
      COL.nbCmd,
      COL.dateExpedition,
      COL.dateLivraison,
    ]);
  }

  // Téléchargement
  const buf = await wb.xlsx.writeBuffer();
  const gen = ctx.generatedAt ?? new Date();
  const stamp = `${gen.getFullYear()}-${String(gen.getMonth() + 1).padStart(2, "0")}-${String(gen.getDate()).padStart(2, "0")}`;
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `couverture-commandes-${stamp}.xlsx`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
